import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ReactSession } from 'react-client-session';

import { loadStripe } from '@stripe/stripe-js';
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';

import { Card, Form, Input, message, Select, Checkbox, Button, Spin, Alert, Popover } from 'antd';
import {
  ArrowLeftOutlined,
  CreditCardOutlined,
  InfoCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  EyeInvisibleOutlined
} from '@ant-design/icons';
import { Col, Row } from 'react-bootstrap';
import './index.css';
import { setOrderId, setPersonalData } from '../store/actionCreators';
import PropTypes from 'prop-types';

import {
  getHdyfu,
  addPersonal,
  addOrder,
  placeOrder,
  stripeCheckout,
  getValidateCoupon,
  updateLoggedUserPersonalDetails,
  packagesLogin
} from '../../../data/API';
import { setStepCount, setDiscount } from '../store/actionCreators';
import { setPackagesCustomer } from '../store/actionCreators';
import { setMyData, setToken } from '../../../utils/token';
import { useNavigate } from 'react-router-dom';
import { handleEventClick } from '../index';
import fetchBubbleContent from '../../../utils';
import TermsServiceModal from './TermsAndServices';

// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PAYMENT_KEY);

const ELEMENTS_OPTIONS = {
  fonts: [
    {
      cssSrc: 'https://fonts.googleapis.com/css?family=Roboto'
    }
  ]
};

const CheckoutForm = (props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();
  const session = ReactSession.get('session');

  const currentStep = useSelector((state) => state.package.currentStep);
  const { customerAddress } = useSelector((state) => state.package);
  const deliveryInfo = useSelector((state) => state.package.deliveryDetails.deliveryInfo);
  const discount = useSelector((state) => state.package.discount);
  const [form] = Form.useForm();
  const [hearingSources, setHearingSources] = useState([]);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [validMessage, setValidMessage] = useState('');
  const [status, setStatus] = useState('');
  const [bubbleContents, setBubbleContents] = useState({});
  const obj = { title: "Driver-Tip" }; 
  const passwordObj = { title: "Password" };
  const { isParentPayClicked, setParentPayClicked } = props;
  const [emailExists, setEmailExists] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userData, setUserData] = useState(null);
  const [isTermsModalVisible, setIsTermsModalVisible] = useState(false);
  const [hasReadTerms, setHasReadTerms] = useState(false);

  const personalData = useSelector((state) => state.package.personalData);
  const packageData = useSelector((state) => state.package);
  const formRef = useRef(null);

  const showTermsModal = () => {
    setIsTermsModalVisible(true);
  };

  const handleTermsModalClose = () => {
    setIsTermsModalVisible(false);
  };

  const handleTermsAccept = (accepted) => {
    if (accepted) {
      setHasReadTerms(true);
      setAgreeTerms(true);
      form.setFieldsValue({ termsAndCondition: true });
    } else {
      setHasReadTerms(false);
      setAgreeTerms(false);
      form.setFieldsValue({ termsAndCondition: false });
    }
    setIsTermsModalVisible(false);
  };

  const changeAgreeTerms = (e) => {
    if (!hasReadTerms) {
      e.preventDefault();
      setIsTermsModalVisible(true);
      // Reset form field to unchecked
      form.setFieldsValue({ termsAndCondition: false });
      return;
    }
    setAgreeTerms(e.target.checked);
    form.setFieldsValue({ termsAndCondition: e.target.checked });
  };

  const handleValueChange = (changedValues, allValues) => {
    if ('email_address' in changedValues) {
      localStorage.setItem('email', changedValues?.email_address);
    }

    if ('driver_tip' in changedValues) {
      const tipValue = changedValues.driver_tip;
      const parsedTip = parseFloat(tipValue) || 0;
      localStorage.setItem('driverTip', parsedTip);
      props.setDriverTip(parsedTip);
    }
  };
  const onFinish = (val) => {
    dispatch(setPersonalData(val));
  };

  const onFinishFailed = () => {
    message.error('Submit failed!');
  };

  const getHearingSources = async () => {
    const response = await getHdyfu();

    if (response?.data?.results) {
      setHearingSources(response?.data?.results);
    } else {
      return message.error('error while fetching hearing sources');
    }
  };

  // Init Invocations
  useEffect(() => {
    getHearingSources();
    if (discount) {
      form.setFieldsValue({ voucher_code: discount?.code });
      setValidMessage('You have successfully applied the promotional code to the order');
      setStatus('success');
    } else {
      form.setFieldsValue({ voucher_code: null });
    }
    window.scrollTo({
      top: props.tabref.current.offsetTop - 100,
      behavior: 'smooth'
    });
  }, []);

  useEffect(() => {
    if (isParentPayClicked) {
      handleClick();
    }
  }, [isParentPayClicked]);
  useEffect(() => {
    return async () => {
      setIsFormSubmitted(false);
    };
  }, []);

  useEffect(() => {
  const token = localStorage.getItem('token');
  const authData = localStorage.getItem('authData');
  
  if (token && authData) {
    setIsLoggedIn(true);
    try {
      const parsedAuthData = JSON.parse(authData);
      setUserData({
        first_name: parsedAuthData.name || '', 
        last_name: '', 
        email_address: parsedAuthData.email || '',
        id: parsedAuthData.id || ''
      });
    } catch (error) {
      console.error('Error parsing authData:', error);
      setIsLoggedIn(false);
    }
  } else {
    setIsLoggedIn(false);
    setUserData(null);
  }
}, []);

  useEffect(() => {
  if (isLoggedIn && userData) {
    const nameParts = userData.first_name.split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    
    const formData = {
      first_name: firstName,
      last_name: lastName,
      email_address: userData.email_address
    };
    
    // Check if navigating from Quick Actions and add mobile numbers
    const urlParams = new URLSearchParams(window.location.search);
    const fromQuickActions = urlParams.get('from') === 'quick-actions';
    if (fromQuickActions) {
      const mobile = urlParams.get('mobile');
      const secondaryMobile = urlParams.get('secondary_mobile');
      if (mobile) formData.phone_number = mobile;
      if (secondaryMobile) formData.secondary_phone_number = secondaryMobile;
    }
    
    form.setFieldsValue(formData);
  }
}, [isLoggedIn, userData, form]);

  const handleClick = async (event) => {
    localStorage.removeItem('email');
    sessionStorage.setItem('orderType', 'packages');
    const stripe = await stripePromise;
    
    try {
      let formValues = await form.validateFields();
      console.log('formValues:', formValues);
      console.log('isLoggedIn:', isLoggedIn);
      console.log('session:', session);
      
      if (formValues.errorFields?.length) {
        return;
      }
      
      if (!isLoggedIn && !emailExists) {
        if (!agreeTerms || !formValues?.termsAndCondition) {
          message.error('Please accept the terms and conditions.');
          return;
        }
        
        // Check if user has read the terms (via modal)
        if (!hasReadTerms) {
          message.error('Please read the terms and conditions first.');
          setIsTermsModalVisible(true); // Show modal to read terms
          return;
        }
        
        // Check if "How did you hear about us" is selected
        if (!formValues?.hdyfu) {
          message.error('Please select how you heard about us.');
          return;
        }
      }
      
      setIsFormSubmitted(true);
      
      let data = {
        first_name: formValues.first_name,
        last_name: formValues.last_name,
        email_address: formValues.email_address,
        phone_number: formValues.phone_number
          .replace('(', '')
          .replace(')', '')
          .replace(' ', '')
          .replace('-', ''),
        session: session
      };
      
      if (!isLoggedIn && !emailExists) {
        data.hdyfu = formValues.hdyfu;
        data.password = formValues.password;
        data.cnf_password = formValues.cnf_password;
        data.termsAccepted = true; // Flag to indicate terms were accepted
      }
      
      if (formValues?.secondary_phone_number) {
        data['secondary_phone_number'] = formValues.secondary_phone_number
          .replace('(', '')
          .replace(')', '')
          .replace(' ', '')
          .replace('-', '');
      }
      
      data['voucher_code'] = formValues.voucher_code;
      
      formRef.current.submit();
      
      let orderdetails = {
        session: session,
        orderType: 'packages',
        delivery_date: deliveryInfo.delivery_date,
        voucher: discount ? discount.code : '',
        driver_tip: props.driverTip || 0
      };
      
      let personalDetailsRes;

      if (isLoggedIn && userData) {
        personalDetailsRes = await updateLoggedUserPersonalDetails(data);
      } else {
        personalDetailsRes = await addPersonal(data);
    
        if (personalDetailsRes?.data && personalDetailsRes.data.token) {
          console.log('User registered successfully with auto-login tokens');
          
          const userData = personalDetailsRes.data;
          setToken(userData.token);
          setMyData({
            email: userData.email_address,
            id: userData.id,
            name: `${userData.first_name} ${userData.last_name}`.trim()
          });
          
          dispatch(setPackagesCustomer({
            userId: userData.id,
            userName: `${userData.first_name} ${userData.last_name}`.trim(),
            token: userData.token,
            email: userData.email_address
          }));
          
          localStorage.setItem('loginType', 'Moving Box Rental');

          setIsLoggedIn(true);
          setUserData({
            first_name: userData.first_name || '',
            last_name: userData.last_name || '',
            email_address: userData.email_address || '',
            id: userData.id || ''
          });
          
        } else {
          console.log('Registration successful but no auto-login tokens received');
        }
      }
      
      
      // Always create a new order regardless of login status
      if (personalDetailsRes?.data) {
        console.log('Creating new order...');
        let orderRes = await addOrder(orderdetails);
        let total = Number(orderRes?.data?.total);
        let totalamount = localStorage.getItem('total');
        
        const stripeSession = await stripeCheckout({
          payment: { billing_details: { email: formValues.email_address } },
          session: orderRes?.data?.order_id,
          order_type: orderdetails.orderType,
          amount: total !== null ? Number(total) : totalamount,
          discount: 0,
          driver_tip: props.driverTip || 0
        });
        
        setIsFormSubmitted(false);
        
        const result = await stripe.redirectToCheckout({
          sessionId: stripeSession.data.sessionId
        });
        
        if (result.error) {
          console.error('Stripe redirect error:', result.error);
          message.error('Payment redirect failed. Please try again.');
          setIsFormSubmitted(false);
        }
      } else {
        throw new Error('Failed to create/update user details');
      }
      
    } catch (error) {
      console.error('Error in handleClick:', error);
      setIsFormSubmitted(false);

      if (error.response?.status === 400) {
        message.error('Invalid data provided. Please check your information.');
      } else if (error.response?.status === 409) {
        message.error('Email already exists. Please use a different email or login.');
      } else if (error.response?.status === 422) {
        message.error('Please fill in all required fields correctly.');
      } else {
        message.error(error.message || 'Something went wrong. Please try again.');
      }
    }
  };

  const prevStep = () => {
    dispatch(setStepCount(currentStep - 1));
  };

  // const changeAgreeTerms = (e) => {
  //   setAgreeTerms(e.target.checked);
  // };

  const validateMobileNumber = (e) => {
    if (!/[0-9]/.test(event.key)) {
      event.preventDefault();
    }
  };

  const handleEmailBlur = async (e) => {
    const email = e.target.value;
    if (email && !isLoggedIn) {
      try {
        const response = await addPersonal({ email_address: email });
        if (response?.data?.exists) {
          setEmailExists(true);
        } else {
          setEmailExists(false);
        }
      } catch (error) {
        console.error('Error checking email:', error);
        setEmailExists(false);
      }
    }
  };

  const formatPhoneNumber = (value) => {
    if (!value) return value;

    const phoneNum = value.replace(/[^\d]/g, '');
    const phoneLen = phoneNum.length;

    if (phoneLen < 4) return phoneNum;

    if (phoneLen < 7) {
      return `(${phoneNum.slice(0, 3)}) ${phoneNum.slice(3)}`;
    }

    return `(${phoneNum.slice(0, 3)}) ${phoneNum.slice(3, 6)}-${phoneNum.slice(6, 10)}`;
  };

  const handlePrimary = (e) => {
    const formattedPhone = formatPhoneNumber(e.target.value);
    form.setFieldsValue({
      phone_number: formattedPhone
    });
  };

  const handleSecondary = (e) => {
    const formattedPhone = formatPhoneNumber(e.target.value);
    form.setFieldsValue({
      secondary_phone_number: formattedPhone
    });
  };

  useEffect(() => {
    localStorage.setItem('package', JSON.stringify(packageData));
    return async () => {
      if (isParentPayClicked) {
        localStorage.removeItem('email');
        localStorage.removeItem('package');
      }
    };
  }, []);
  const test = () => {
    getValidateCoupon(form.getFieldValue('voucher_code'))
      .then((response) => {
        setValidMessage('You have successfully applied the promotional code to the order');
        setStatus('success');
        dispatch(setDiscount(response.data));
      })
      .catch((err) => {
        let status = err.response.status;
        if (status == 404) {
          setValidMessage('The coupon code you entered is not valid.');
        } else if (status == 400) {
          setValidMessage('The coupon code you have entered has expired.');
        } else {
          setValidMessage('Network connnection. Please try again');
        }
        setStatus('error');
        dispatch(setDiscount(null));
      });
  };

  useEffect(() => {
    const savedTip = localStorage.getItem('driverTip');
    if (savedTip && savedTip !== 'null') {
      const parsedTip = parseFloat(savedTip);
      form.setFieldsValue({ driver_tip: parsedTip });
      props.setDriverTip(parsedTip);
    }
  }, []);

  
  return (
    <div>
      <div className="steps bg-white" id="steps">
        <h2 className="shopping-cart-header text-white text-center py-2 fs-20 steps-title">
          Step 6: Personal & Payment Details
        </h2>
        <Card bordered={false} className="w-100 p-3">
          <Spin spinning={isFormSubmitted} tip="processing...">
            <div className="mb-4">
              <p className="mb-2 text-left fs-20 fw-500">Personal Details</p>
            </div>
            <Form
              onValuesChange={handleValueChange}
              form={form}
              layout="vertical"
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              autoComplete="off"
              initialValues={personalData}
              style={{ position: 'relative', zIndex: 0 }}
              ref={formRef}>
              <Row>
                <Col className="px-3 py-0" xxl={6} xl={6} lg={6} md={6} sm={12} xs={12}>
                  <Form.Item
                    name="first_name"
                    label="First Name"
                    rules={[{ required: true, message: 'Please input the first name' }]}>
                    <Input 
                      size="large" 
                      placeholder="" 
                      readOnly={isLoggedIn}
                      style={isLoggedIn ? { backgroundColor: '#f5f5f5', cursor: 'default', color: '#a0a0a0' } : {}}
                    />
                  </Form.Item>
                </Col>
                <Col className="px-3 py-0" xxl={6} xl={6} lg={6} md={6} sm={12} xs={12}>
                  <Form.Item
                    name="last_name"
                    label="Last Name"
                    rules={[{ required: true, message: 'Please input the last name' }]}>
                    <Input 
                      size="large" 
                      placeholder="" 
                      readOnly={isLoggedIn}
                      style={isLoggedIn ? { backgroundColor: '#f5f5f5', cursor: 'default', color: '#a0a0a0' } : {}}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row>
                <Col className="px-3 py-0" xxl={6} xl={6} lg={6} md={6} sm={12} xs={12}>
                  <Form.Item
                    name="email_address"
                    label="Email Address"
                    validateTrigger="onBlur"
                    rules={[
                      { required: true, message: 'Please input the email address' },
                      { type: 'email', message: 'Please enter a valid email address' }
                    ]}
                  >
                    <Input
                      size="large"
                      placeholder=""
                      readOnly={isLoggedIn}
                      style={isLoggedIn ? { backgroundColor: '#f5f5f5', cursor: 'default', color: '#a0a0a0' } : {}}
                      onBlur={!isLoggedIn ? handleEmailBlur : undefined}
                      onChange={(e) => {
                        if (!isLoggedIn && emailExists) {
                          setEmailExists(false);
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col className="px-3 py-0" xxl={6} xl={6} lg={6} md={6} sm={12} xs={12}>
                  <Form.Item
                    name="phone_number"
                    label="Mobile Number"
                    validateTrigger="onBlur"
                    extra="(Used for delivery and pick up notifications)"
                    rules={[
                      { required: true, message: 'Please input the mobile number', min: 14 }
                    ]}>
                    <Input
                      size="large"
                      placeholder=""
                      minLength={14}
                      maxLength={14}
                      onKeyPress={(event) => {
                        validateMobileNumber(event);
                      }}
                      onChange={(e) => handlePrimary(e)}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row>
                <Col className="px-3 py-0" xxl={6} xl={6} lg={6} md={6} sm={12} xs={12}>
                  <Form.Item
                    name="secondary_phone_number"
                    label="Secondary phone number"
                    validateTrigger="onBlur"
                    rules={[
                      ({ getFieldValue }) => ({
                        validator(rule, value) {
                          if (
                            getFieldValue('secondary_phone_number') &&
                            getFieldValue('secondary_phone_number').length != 14
                          ) {
                            return Promise.reject('Please enter valid number'); // The validator should always return a promise on both success and error
                          } else {
                            return Promise.resolve();
                          }
                        }
                      })
                    ]}>
                    <Input
                      size="large"
                      placeholder=""
                      minLength={14}
                      maxLength={14}
                      onKeyPress={(event) => {
                        validateMobileNumber(event);
                      }}
                      onChange={(e) => handleSecondary(e)}
                    />
                  </Form.Item>
                </Col>
                {/* When NOT logged in: Show Driver Tip first */}
                {!isLoggedIn && (
                  <Col className="px-3 py-0" xxl={4} xl={4} lg={4} md={4} sm={8} xs={8}>
                    <Form.Item
                      name="driver_tip"
                      className='tool-tip'
                      label={
                        <div style={{ display: "flex", width: "100%", alignItems: "center", gap: "10px" }}>
                          <span>Add a Driver Tip (Optional)</span>
                          <Popover 
                            content={
                              <div className="box-popover">
                                <p>{bubbleContents[obj.title] || 'Loading...'}</p>
                              </div>
                            }
                            trigger="hover"
                            onVisibleChange={(visible) => {
                              if (visible) {
                                console.log("Fetching content for:", obj.title);
                                fetchBubbleContent(obj.title, setBubbleContents);
                              }
                            }}
                          >
                            <i className="fa fa-info-circle color-primary" aria-hidden="true"></i>
                          </Popover>
                        </div>
                      }
                    >
                      <Input size="large" placeholder="Enter Tip Amount" />
                    </Form.Item>
                  </Col>
                )}
                {/* When logged in: Show original order */}
                {isLoggedIn && (
                  <Col className="px-3 py-0" xxl={4} xl={4} lg={4} md={4} sm={8} xs={8}>
                    <Form.Item
                      name="voucher_code"
                      label="If you have a coupon, enter it below"
                      validateStatus={status}>
                      <Input size="large" placeholder="" />
                    </Form.Item>
                  </Col>
                )}
                {isLoggedIn && (
                  <Col className="px-3 py-0 apply-button" xxl={2} xl={2} lg={2} md={2} sm={4} xs={4}>
                    <Form.Item name="voucher_code_apply" label=" ">
                      <Button size="large" block onClick={test}>
                        Apply
                      </Button>
                    </Form.Item>
                  </Col>
                )}
                

                {/* {status === 'success' && (
                  <Alert
                      message={message}
                      type={status}
                      showIcon
                      icon={<InfoCircleOutlined />}
                  />
                )} */}
              </Row>
              {!isLoggedIn && !emailExists && (
                <Row gutter={16}>
                  <Col xxl={6} xl={6} lg={6} md={6} sm={12} xs={12} style={{ paddingBottom: 0 }}>
                    <div>
                      <Form.Item
                        label={
                          <div style={{ display: "flex", width: "100%", alignItems: "center", gap: "10px" }}>
                            <span>Password</span>
                            <Popover 
                              content={
                                <div className="box-popover">
                                  <p>{bubbleContents[passwordObj.title] || 'Loading...'}</p>
                                </div>
                              }
                              trigger="hover"
                              onVisibleChange={(visible) => {
                                if (visible) {
                                  console.log("Fetching content for: password");
                                  fetchBubbleContent(passwordObj.title, setBubbleContents);
                                }
                              }}
                            >
                              <i className="fa fa-info-circle color-primary" aria-hidden="true"></i>
                            </Popover>
                          </div>
                        }
                        name="password"
                        rules={[
                          {
                            required: false,
                            message: 'Please enter your password!',
                          },
                        ]}
                      >
                        <Input.Password
                          size="large"
                          style={{
                            height: '40px',
                            width: '100%',
                          }}
                          iconRender={(visible) =>
                            visible ? <EyeInvisibleOutlined /> : <EyeOutlined />
                          }
                        />
                      </Form.Item>
                    </div>
                  </Col>
                  <Col xxl={6} xl={6} lg={6} md={6} sm={12} xs={12} style={{ paddingBottom: 0 }}>
                    <div>
                      <Form.Item
                        label="Confirm Password"
                        name="cnf_password"
                        dependencies={['password']}
                        rules={[
                          {
                            required: false,
                            message: 'Please confirm your password!',
                          },
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              if (!value || getFieldValue('password') === value) {
                                return Promise.resolve();
                              }
                              return Promise.reject(
                                new Error('The two passwords that you entered do not match!')
                              );
                            },
                          }),
                        ]}
                      >
                        <Input.Password
                          size="large"
                          style={{
                            height: '40px',
                            width: '100%',
                          }}
                          iconRender={(visible) =>
                            visible ? <EyeInvisibleOutlined /> : <EyeOutlined />
                          }
                        />
                      </Form.Item>
                    </div>
                  </Col>
                </Row>
              )}
              {!isLoggedIn && (
                <Row>
                  {!emailExists && (
                    <Col className="px-3 py-0" xxl={6} xl={6} lg={6} md={6} sm={12} xs={12}>
                      <Form.Item
                        name="hdyfu"
                        label="How did you hear about us?"
                        rules={[{ required: true, message: 'Please input how you hear about us?' }]}>
                        <Select size="large" onClick={handleEventClick}>
                          {hearingSources?.map((obj, index) => {
                            return (
                              <Select.Option key={`source_${index}`} value={obj?.id}>
                                {obj.title}
                              </Select.Option>
                            );
                          })}
                        </Select>
                      </Form.Item>
                    </Col>
                  )}

                  <div  style={{ marginLeft: '12px'}}>
                    <label style={{ display: 'block', marginBottom: '8px' }}>
                      If you have a coupon, enter it below
                    </label>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'stretch' }}>
                      <Form.Item
                        name="voucher_code"
                        validateStatus={status}
                        style={{ flex: 1, marginBottom: 0 }}
                      >
                        <Input size="large" placeholder="" />
                      </Form.Item>
                      <Button size="large" onClick={test} style={{ minWidth: '100px' }}>
                        Apply
                      </Button>
                    </div>
                  </div>

                  <div className='w-100'>
                  <Col className="px-3 py-0 d-none" xxl={6} xl={6} lg={6} md={6} sm={0} xs={0}></Col>
                  <Col className="px-3 py-0 ml-auto mb-3" xxl={6} xl={6} lg={6} md={6} sm={12} xs={12}>
                    {status && validMessage && ['success', 'error', 'warning', 'info'].includes(status) && (
                      <Alert
                        message={validMessage}
                        type={status}
                        showIcon
                        icon={status === 'success' ? <InfoCircleOutlined /> : <CloseCircleOutlined />}
                      />
                    )}
                  </Col>
                </div>

                </Row>
              )}

              {isLoggedIn && (
                <Row>
                  <Col className="px-3 py-0" xxl={6} xl={6} lg={6} md={6} sm={12} xs={12}>
                    <Form.Item
                      name="driver_tip"
                      className='tool-tip'
                      label={
                        <div style={{ display: "flex", width: "100%", alignItems: "center", gap: "10px" }}>
                          <span>Add a Driver Tip (Optional)</span>
                          <Popover 
                            content={
                              <div className="box-popover">
                                <p>{bubbleContents[obj.title] || 'Loading...'}</p>
                              </div>
                            }
                            trigger="hover"
                            onVisibleChange={(visible) => {
                              if (visible) {
                                console.log("Fetching content for:", obj.title);
                                fetchBubbleContent(obj.title, setBubbleContents);
                              }
                            }}
                          >
                            <i className="fa fa-info-circle color-primary" aria-hidden="true"></i>
                          </Popover>
                        </div>
                      }
                    >
                      <Input size="large" placeholder="Enter Tip Amount" />
                    </Form.Item>
                  </Col>
                  {validMessage.length > 0 && (
                  <Col className="px-3 py-0" xxl={6} xl={6} lg={6} md={6} sm={12} xs={12}>
                      <Alert
                        message={validMessage}
                        type={status}
                        showIcon
                        icon={status === 'success' ? <InfoCircleOutlined /> : <CloseCircleOutlined />}
                      />
                    </Col>
                )}
                </Row>
              )}
              {!isLoggedIn && !emailExists && (
                <Row>
                  <Col
                    className="px-3 py-0 d-flex"
                    xxl={6}
                    xl={6}
                    lg={6}
                    md={6}
                    sm={12}
                    xs={12}>
                  </Col>
                  <Col
                    className="px-3 py-0 d-flex "
                    xxl={6}
                    xl={6}
                    lg={6}
                    md={6}
                    sm={12}
                    xs={12}>
                    <Form.Item
                      className="mb-0"
                      name="termsAndCondition"
                      valuePropName="checked"
                      rules={[{ required: true, message: 'Please accept the terms and condition' }]}>
                      <Checkbox
                        className="fs-15"
                        checked={agreeTerms}
                        onChange={changeAgreeTerms}>
                        I agree to{' '}
                        <span 
                          onClick={showTermsModal} 
                          className="agree-term" 
                          style={{ 
                            cursor: 'pointer', 
                            color: '#1890ff', 
                            textDecoration: 'underline',
                            fontWeight: '500'
                          }}
                        >
                          BlueBox Terms of Service
                        </span>
                      </Checkbox>
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </Form>
          </Spin>
        </Card>
      </div>

      <div className="row mt-4 mb-10 p-2">
        <div className="col d-flex justify-content-start align-items-center">
          <Button
            className="btn-previous-step red-border-button"
            icon={<ArrowLeftOutlined />}
            onClick={prevStep}
            size={'large'}>
            {/* <img src='img/Previous.png' /> */}
            Previous Step
          </Button>
        </div>
        <div className="col  d-flex justify-content-end align-items-center">
          <Button
            className="btn-submit-order red-button"
            icon={<CreditCardOutlined />}
            onClick={handleClick}
            size={'large'}
            loading={isFormSubmitted}>
            {!isFormSubmitted ? 'Pay & Order' : 'Redirecting...'}
          </Button>
        </div>
      </div>
      
      <TermsServiceModal 
        visible={isTermsModalVisible} 
        onClose={handleTermsModalClose} 
        onAccept={handleTermsAccept}
      />
    </div>
  );
};

CheckoutForm.propTypes = {
  isParentNextStepClicked: PropTypes.any,
  tabref: PropTypes.any,
  isParentPayClicked: PropTypes.any,
  setParentPayClicked: PropTypes.func,
  setDriverTip: PropTypes.func,
  driverTip: PropTypes.oneOfType([PropTypes.string, PropTypes.number])  
};

const PersonalPayDetails = (props) => {
  return (
    <>
      <Elements stripe={stripePromise} options={ELEMENTS_OPTIONS}>
        <CheckoutForm
          tabref={props.tabref}
          isParentPayClicked={props.isParentPayClicked}
          setParentPayClicked={props.setParentPayClicked}
          setDriverTip={props.setDriverTip}
          driverTip={props.driverTip}
        />
      </Elements>
    </>
  );
};

PersonalPayDetails.propTypes = {
  isParentNextStepClicked: PropTypes.any,
  tabref: PropTypes.any,
  isParentPayClicked: PropTypes.any,
  setParentPayClicked: PropTypes.func,
  setDriverTip: PropTypes.func,
  driverTip: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default PersonalPayDetails;
